import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useRef, useEffect } from 'react';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { ICatalogSectionItemBlock } from '@features/cms/typescript/index';
import styles from '@features/cms/components/pages/CatalogSectionBlocks/CatalogSectionBlocks.styles';
import scrollToElementById from '@utils/scrollToElementById';
import RenderMedia from '@utils/RenderMedia';
type PropsType = {
  item: ICatalogSectionItemBlock;
  layout: string;
  isFirst: boolean;
  getTextColor: () => string;
  getTextHoverColor: () => string;
  getBgColor: () => string;
  getBgHoverColor: () => string;
  getImage: () => string;
  getVideo: () => string;
};
const CatalogSectionItemDescriptionBgImg = ({
  item,
  layout,
  isFirst,
  getTextColor,
  getTextHoverColor,
  getBgColor,
  getBgHoverColor,
  getImage,
  getVideo
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    header,
    description,
    lineSpacing,
    filePath,
    fileTarget,
    buttonVariant,
    buttonText,
    image,
    video
  } = item;
  const containerRef = useRef<HTMLDivElement>(null);

  // WCAG 2.4.7 Focus Visible - dodaj klasę na kontenerze gdy link/button ma focus-visible
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.matches('a:focus-visible, button:focus-visible')) {
        container.classList.add('has-focus-visible');
      }
    };
    const handleFocusOut = () => {
      container.classList.remove('has-focus-visible');
    };
    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  const renderContent = (isLink: boolean) => <>
      <div sx={styles.catalogCard(isLink, (!!description || !!header) && getImage() === 'none' && getVideo() === 'none', 1, getTextColor(), getBgColor())} data-testid="catalog-section-item-description-bg-img">
        <Heading h={isFirst ? 1 : 3} headingType={3} mobile color={getTextHoverColor()}>
          {header}
        </Heading>
        <div sx={styles.descriptionContainer(getTextColor(), lineSpacing)} dangerouslySetInnerHTML={{
        __html: description || ''
      }} data-testid="catalog-section-item-description-bg-img-content" />
      </div>
      {filePath && <div sx={styles.styledViewFileLink(getTextHoverColor(), buttonVariant !== 'noIcon')} data-testid="catalog-section-item-description-bg-img-button">
          {buttonVariant !== 'noIcon' && <Icon icon="Download2" />}
          <Heading h={6} headingType={6} whiteSpace="nowrap" color={getTextHoverColor()} uppercase mobile>
            {buttonText || t('download')}
          </Heading>
        </div>}
    </>;
  return <div ref={containerRef} sx={{
    ...styles.styledLayout(layout, !!description || !!header, 'descriptionBgImg', getTextColor(), getTextHoverColor(), getBgColor(), getBgHoverColor()).item,
    backgroundImage: !getVideo() ? getImage() : 'none'
  }} data-testid="catalog-section-item-description-bg-img-container">
      {RenderMedia(image, video, true, undefined, header, false)}
      {filePath ? <>
          {filePath.slice(0, 1) === '#' ? <button type="button" onClick={() => scrollToElementById(filePath.substring(1))} data-testid={`catalogSectionItem-${header}-button`}>
              {renderContent(true)}
            </button> : <Link href={filePath} target={fileTarget || '_blank'} rel={!fileTarget || fileTarget === '_blank' ? 'noopener noreferrer' : ''}>
              {renderContent(true)}
            </Link>}
        </> : <span>{renderContent(false)}</span>}
    </div>;
};
export default CatalogSectionItemDescriptionBgImg;